import { useRef, useCallback, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import FiltersApi from 'libs/api/filters';
import { atom, useRecoilState } from 'recoil';
import { clearSavedListSearches } from 'helpers/listSearchStorage';
const defaultState = {
  by_calendar_categories: [],
  brands: [],
  online_brands: [],
  chains: [],
  companies: [],
  inactive_companies: [],
  company_categories: [],
  day_types: [],
  macro_categoris: [],
  prestige_levels: [],
  product_sheet_models: [],
  products_limits: [],
  roles: [],
  shopping_centers: [],
  tax_frees: [],
  type_closes: [],
  leasings_assets: [],
  leasings_contact_roles: [],
  leasings_contacts: [],
  leasings_managers: [],
  leasings_rooms: [],
  allCompanies: [],
  success: false,
  loading: true,
  leasings_meeting_types: [],
};

// Filters loaded without shopping center restriction. Pages with
// `filterByShoppingCenter` replace filterState with a filtered response, so
// this snapshot is used to restore it when the village is cleared or the
// page is left (otherwise other pages keep the filtered "Insegna" list).
let unfilteredFilterData = null;

// /filters request in flight: several components call useFilter on the
// same page, they all wait for it instead of each sending its own request.
let initialLoadPromise = null;
// Bumped on logout so a request started before it doesn't set old data
let loadGeneration = 0;

const withShopTitles = (list) =>
  list?.map((i) => ({
    ...i,
    title: `${i.title} (${i.shopping_center_title})`,
  }));

export const filterState = atom({
  key: 'filterState',
  default: defaultState,
});

export const useFilter = ({
  page = '',
  filterByShoppingCenter = false,
  companyCategoriesIds = [],
} = {}) => {
  let [searchParams, setSearchParams] = useSearchParams();
  const apiRef = useRef(new FiltersApi(page));
  const [filterData, setFilterData] = useRecoilState(filterState);
  const [filterLoading, setFilterLoading] = useState(false);
  useEffect(() => {
    if (global.isLoadedFilters) return undefined;
    let active = true;
    setFilterLoading(true);

    if (!initialLoadPromise) {
      const generation = loadGeneration;
      setFilterData((state) => ({ ...state, loading: false }));
      initialLoadPromise = apiRef.current.list().then((filters) => {
        if (generation !== loadGeneration) return null;
        unfilteredFilterData = {
          ...filters.data,
          companies: withShopTitles(filters?.data?.companies),
          inactive_companies: withShopTitles(filters?.data?.inactive_companies),
          allCompanies: withShopTitles(filters?.data?.all_companies),
          brands: withShopTitles(filters?.data?.brands),
        };
        setFilterData(unfilteredFilterData);
        global.isLoadedFilters = true;
        return unfilteredFilterData;
      });
      // share only the request in flight; after it settles a new mount
      // either sees isLoadedFilters or (after a failure) requests again
      const settled = () => {
        if (generation === loadGeneration) initialLoadPromise = null;
      };
      initialLoadPromise.then(settled, settled);
    }

    initialLoadPromise
      .catch(() => {})
      .then(() => {
        if (active) setFilterLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (filterByShoppingCenter) {
      if (
        searchParams.get('by_shopping_center') ||
        searchParams.get('shopping_center_id') ||
        searchParams.get('shopping_center')
      ) {
        setFilterLoading(true);
        let params = {
          params: {},
        };
        const searchParamsObj = Object.fromEntries([...searchParams]);
        params.params.by_shopping_center =
          searchParamsObj.by_shopping_center ||
          searchParamsObj.shopping_center_id ||
          searchParams.get('shopping_center');
        (async () => {
          try {
            const filters = await apiRef.current.list(params);
            // the full list must not overwrite the filtered one afterwards
            if (initialLoadPromise) {
              await initialLoadPromise.catch(() => {});
            }
            if (cancelled) return;
            setFilterData({
              ...filters.data,
              companies: withShopTitles(filters?.data?.companies),
              inactive_companies: withShopTitles(
                filters?.data?.inactive_companies,
              ),
            });
          } catch (e) {
            // keep current lists; the error is shown by the request layer
          } finally {
            if (!cancelled) setFilterLoading(false);
          }
        })();
      } else if (unfilteredFilterData) {
        setFilterData(unfilteredFilterData);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [
    filterByShoppingCenter,
    searchParams.get('by_shopping_center'),
    searchParams.get('shopping_center_id'),
    searchParams.get('shopping_center'),
  ]);

  // Leaving a page that filtered by shopping center: restore full filters
  useEffect(() => {
    if (!filterByShoppingCenter) return undefined;
    return () => {
      if (unfilteredFilterData) {
        setFilterData(unfilteredFilterData);
      }
    };
  }, [filterByShoppingCenter, setFilterData]);

  const handleChangeFilters = useCallback(
    (filter, e) => {
      const { type, keys, name } = filter;
      const currentParams = Object.fromEntries([...searchParams]);
      if (type === 'input') {
        const { value } = e.target;
        if (value) {
          setSearchParams({
            ...currentParams,
            [name]: value,
          });
        } else {
          setSearchParams({
            ...currentParams,
            [name]: '',
          });
        }
      } else if (type === 'range') {
        if (e) {
          setSearchParams({
            ...currentParams,
            [keys[0]]: e[0].format('DD-MM-YYYY'),
            [keys[1]]: e[1].format('DD-MM-YYYY'),
          });
        } else {
          setSearchParams({
            ...currentParams,
            [keys[0]]: '',
            [keys[1]]: '',
          });
        }
      } else if (type === 'select') {
        //fix filter company when shopping center is changed
        if (name === 'shopping_center_id') {
          if (currentParams.company) {
            if (e !== currentParams.shopping_center_id) {
              setSearchParams({
                ...currentParams,
                [name]: e,
                company: '',
              });
            }
          } else {
            setSearchParams({
              ...currentParams,
              [name]: e,
            });
          }
        } else {
          setSearchParams({
            ...currentParams,
            [name]: e,
          });
        }
      } else if (type === 'date') {
        setSearchParams({
          ...currentParams,
          // e is null when the date is cleared
          [name]: e ? e.format(filter.format || 'DD-MM-YYYY') : '',
        });
      } else if (type === 'checkbox') {
        const { checked } = e.target;
        const checkedValue = filter.paramValue || 1;
        let notCheckedParamValue = '0';
        if (filter.notCheckedParamValue === '') {
          notCheckedParamValue = '';
        } else if (filter.notCheckedParamValue) {
          notCheckedParamValue = filter.notCheckedParamValue;
        }
        setSearchParams({
          ...currentParams,
          [name]: checked ? checkedValue : notCheckedParamValue,
        });
      }
    },
    [searchParams, setSearchParams],
  );

  const handleCloseFilter = useCallback(
    (filter) => {
      const currentParams = Object.fromEntries([...searchParams]);
      if (filter.type === 'range') {
        filter.keys.forEach((i) => {
          delete currentParams[i];
        });
      } else {
        delete currentParams[filter.name];
      }
      setSearchParams({
        ...currentParams,
      });
    },
    [searchParams, setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setFilterData(defaultState);
    unfilteredFilterData = null;
    initialLoadPromise = null;
    loadGeneration += 1;
    clearSavedListSearches();
    global.isLoadedFilters = false;
  }, []);

  return {
    searchParams,
    filterData,
    setFilterData,
    filterLoading,
    handleChangeFilters,
    handleCloseFilter,
    handleClearFilters,
  };
};
